import { useLayoutEffect, useState } from "react";
import {
    EMPTY_SHARED_GUTTER,
    type OwnGutter,
    type SharedGutter,
    type WidthGroupRegistry,
} from "./width-group-registry";

/**
 * Publish this member’s left/right need and subscribe to the group’s shared gutter
 * (re-renders when another member needs more space).
 *
 * Every interaction with the registry happens in a layout effect, so members agree on the gutter in the
 * frame they mount in. StrictMode double-mount is safe because register/unregister are keyed map updates.
 *
 * First consumer: data-viewer panels aligning plot gutters. The hook itself has no chart imports.
 *
 * With `enabled` false the member stays out of the group and gets the empty gutter back.
 */
export function useSharedGutter(
    registry: WidthGroupRegistry,
    groupId: string,
    memberId: string,
    ownGutter: OwnGutter,
    enabled = true,
): SharedGutter {
    const [sharedGutter, setSharedGutter] = useState(() => registry.getSharedGutter(groupId));

    // Layout-timed rather than useSyncExternalStore, which subscribes in a passive effect React can run after
    // a paint — members would paint their pre-publish gutter and jump a frame later (see README).
    //
    // The re-read catches siblings that published earlier in this commit's layout phase; a member whose own
    // gutter is {0,0} publishes nothing and is never notified, so this is its only way to learn the group's
    // value. Declared above the publish effect so this member is already listening when it publishes.
    useLayoutEffect(() => {
        if (!enabled) return undefined;
        setSharedGutter(registry.getSharedGutter(groupId));
        return registry.subscribe(groupId, setSharedGutter);
    }, [registry, groupId, enabled]);

    // Compare left/right values, not object identity — callers often build a fresh object each render.
    // Update without unregistering: effect cleanup would briefly drop this member from the shared
    // gutter and notify siblings with a too-small value before setOwnGutter ran again.
    const { left, right } = ownGutter;
    useLayoutEffect(() => {
        if (!enabled) {
            registry.unregister(groupId, memberId);
            return;
        }
        registry.setOwnGutter(groupId, memberId, { left, right });
    }, [registry, groupId, memberId, left, right, enabled]);

    useLayoutEffect(
        () => () => {
            registry.unregister(groupId, memberId);
        },
        [registry, groupId, memberId],
    );

    return enabled ? sharedGutter : EMPTY_SHARED_GUTTER;
}
